import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/AuthContext";
import {API} from "@/App";

function LoginPage() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [info, setInfo] = useState(""); // success message for forgot password
    const [isLoading, setIsLoading] = useState(false);
    const [forgotLoading, setForgotLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    useEffect(() => {
        if (user) navigate("/");
    }, [user, navigate]);

    useEffect(() => {
        const storedTime = localStorage.getItem("forgotCooldown");
        if (storedTime) {
            const diff = Math.floor((Date.now() - parseInt(storedTime)) / 1000);
            if (diff < 120) {
                setCooldown(120 - diff);
            }
        }
    }, []);

    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setInterval(() => {
            setCooldown((prev) => prev - 1);
        }, 1000);
        return () => clearInterval(timer);
    }, [cooldown]);


    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setInfo("");
        if (!email || !password) {
            setErr("Please enter both email and password.");
            return;
        }
        setIsLoading(true);
        try {
            await login(email, password);
            navigate("/");
        } catch (ex) {
            setErr(ex.response?.data?.detail || "An unexpected error occurred.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (cooldown > 0) return; // Prevent spam

        if (!email) {
            setErr("Enter your email first to reset password.");
            return;
        }

        setErr("");
        setInfo("");
        setForgotLoading(true);

        try {
            const res = await fetch(`${API}/auth/forgotPassword`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });

            if (res.ok) {
                setInfo("If this User exists, password reset instructions were sent.");

                // 🔥 Start Cooldown
                localStorage.setItem("forgotCooldown", Date.now().toString());
                setCooldown(120);
            } else {
                const data = await res.json();
                setErr(data?.detail || "Failed to send reset link.");
            }
        } catch {
            setErr("Network error. Please try again.");
        } finally {
            setForgotLoading(false);
        }
    };

    return (
        <div className="w-full lg:grid lg:min-h-screen lg:grid-cols-2">
            {/* Left Side: Form */}
            <div className="flex items-center justify-center py-12">
                <div className="mx-auto grid w-[350px] gap-6">
                    <div className="grid gap-2 text-center">
                        <h1 className="text-3xl font-bold">APMS</h1>
                    </div>
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-2xl">Login</CardTitle>
                            <CardDescription>
                                Enter your credentials to access your account.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={submit} className="grid gap-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="you@example.com"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={isLoading || forgotLoading}
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="password">Password</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="••••••••"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={isLoading || forgotLoading}
                                    />
                                </div>

                                {/* Error */}
                                {err && (
                                    <div className="flex items-center gap-x-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                                        <AlertCircle className="h-4 w-4" />
                                        <p>{err}</p>
                                    </div>
                                )}

                                {/* Success */}
                                {info && (
                                    <div className="flex items-center gap-x-2 rounded-md bg-green-100 p-3 text-sm text-green-700">
                                        <CheckCircle2 className="h-4 w-4" />
                                        <p>{info}</p>
                                    </div>
                                )}

                                <Button type="submit" className="w-full" disabled={isLoading}>
                                    {isLoading ? "Logging in..." : "Login"}
                                </Button>

                                {/* Forgot Password Link */}
                                <button
                                    type="button"
                                    onClick={handleForgotPassword}
                                    className="text-sm text-muted-foreground hover:underline disabled:opacity-50"
                                    disabled={forgotLoading || isLoading || cooldown > 0}
                                >
                                    {cooldown > 0
                                        ? `Wait ${Math.floor(cooldown / 60)
                                            .toString()
                                            .padStart(2, "0")}:${(cooldown % 60)
                                            .toString()
                                            .padStart(2, "0")} to resend`
                                        : forgotLoading
                                            ? "Sending reset link..."
                                            : "Forgot your password?"}
                                </button>

                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Right Side: Image */}
            <div className="hidden bg-muted lg:block">
                <img
                    src="/toconvert.png"
                    alt="Architectural design of a modern house"
                    className="h-full w-full object-cover dark:brightness-[0.3]"
                />
            </div>
        </div>
    );
}

export default LoginPage;
